import type ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import TripleToggle from '@/components/Buttons/TrippleToggleButton';
import { applyReportingScreenBundling, isReportingScreenEnabled } from '@/constants/userManagement/reportingScreens';
import { USER_MANAGEMENT_ACCESS_CONTROL_RULES } from '@/constants/validationRules/accessContol';
import { type Control, Controller, type FieldValues, useFormContext } from 'react-hook-form';

interface IProps {
  allowBoth?: boolean;
  control: Control<FieldValues, any, FieldValues>;
  label: string;
  value: ScreenKey;
}

const TripleToggleControl = ({ control, label, value, allowBoth = false }: IProps) => {
  const { setValue } = useFormContext();

  return (
    <Controller
      rules={USER_MANAGEMENT_ACCESS_CONTROL_RULES}
      control={control}
      defaultValue={false}
      name={`accessManagement.${value}`}
      // No shouldUnregister (flank#4749): the User Type dropdown re-chunks
      // this grid per role (screensByRole differs by role), so a screen this
      // control renders for can unmount/remount or move to a different column
      // on every switch. shouldUnregister used to wipe the field's value on
      // that unmount, so switching back to a previous User Type reset every
      // screen to Off instead of restoring what was set. Leaving it unset
      // (RHF's default) keeps the value in form state across the switch;
      // userService.getUserPayload prunes anything outside the CURRENTLY
      // selected role's own screen list before it's ever submitted, so a
      // screen that's no longer applicable can't leak into the saved payload.
      render={({ field }) => (
        <TripleToggle
          fieldValue={field.value}
          screenName={value}
          onChange={newValue => {
            field.onChange(newValue);
            applyReportingScreenBundling(value, isReportingScreenEnabled(newValue), setValue, { allowBoth });
          }}
          label={label}
        />
      )}
    />
  );
};

export default TripleToggleControl;
